import { projectString } from '../project/scalars'
import type {
  AppDoc,
  AppRef,
  CapabilityRef,
  Layout,
  SettingSpec,
  SettingValue,
  Slot,
  SlotFill,
} from './types'

/** An app catalog entry as a picker/prompt sees it: the thin `AppRef` plus its
 *  appId (the map key, which is the app document's id). */
export interface AppSummary extends AppRef {
  id: string
  /**
   * WHICH CATALOG holds it (RFC 0295, S6f).
   *
   * Set by `installedApps`, absent from `projectApps` and `worldApps`, which
   * each answer about one catalog and have nothing to disambiguate.
   *
   * It exists because UNINSTALLING needs it. `unregisterApp` takes the catalog
   * to remove from, and a merged list gave a caller no way to know which. The
   * Apps modal was passing the project for every row, so removing a
   * World-installed sidebar silently did nothing: it edited a map that never
   * held the entry.
   */
  scope?: 'world' | 'project'
}
/** The full app record, from its own document snapshot (RFC 0267). `null` for a
 *  snapshot that is not an app doc. What a caller reads when it needs the schema
 *  or capabilities the thin catalog entry omits (e.g. bridge capability checks). */
export function readApp(appDoc: unknown): AppDoc | null {
  if (!appDoc || typeof appDoc !== 'object') return null
  const d = appDoc as Partial<AppDoc>
  // Both an app doc and a typed INSTANCE carry `dataType`, so discriminate on a
  // field only an app doc has (its capabilities) — an instance is not an app.
  if (typeof d.dataType !== 'string' || !d.dataType) return null
  if (!d.capabilities || typeof d.capabilities !== 'object') return null
  return d as AppDoc
}
/**
 * Project a project's app catalog (RFC 0267), most-recently-updated first.
 * Reads the `apps` map ONLY (no view/skill hydration), so a picker or the
 * `<available_apps>` prompt block renders from the project doc alone. `[]` for
 * a project with no apps.
 */
/**
 * The same value with every stored string read back as a plain string.
 *
 * A string stored as a scalar comes back as an ImmutableString, which is NOT a
 * JS string: `a.presentation === 'layout'` and `a.handles?.includes(mime)` are
 * both false against one, with no error, and `a.name.toLowerCase()` throws.
 * Unwrapped ONCE here, where the catalog enters the model, so every reader
 * below compares plain strings as it always did.
 *
 * Duck-typed rather than `instanceof`: the window and the daemon can hold
 * different automerge instances, and a wrapper from one is not an instance of
 * the other's class.
 */
function unwrapStored(value: unknown, depth = 0): unknown {
  if (!value || typeof value !== 'object') return value
  const keys = Object.keys(value)
  if (keys.length === 1 && keys[0] === 'val') {
    const inner = (value as { val?: unknown }).val
    if (typeof inner === 'string') return inner
  }
  // A guard, not a limit anyone should reach: a catalog entry is a handful of
  // levels deep and a cycle off the log would otherwise hang the window.
  if (depth >= 12) return value
  if (Array.isArray(value)) return value.map((v) => unwrapStored(v, depth + 1))
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = unwrapStored(v, depth + 1)
  }
  return out
}

export function projectApps(projectDoc: unknown): AppSummary[] {
  if (!projectDoc || typeof projectDoc !== 'object') return []
  const apps = (projectDoc as { apps?: unknown }).apps
  if (!apps || typeof apps !== 'object') return []
  const out: AppSummary[] = []
  for (const [id, raw] of Object.entries(apps as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue
    out.push({ id, ...(unwrapStored(raw) as AppRef) })
  }
  return out.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
}
/**
 * The apps installed for the whole deployment (RFC 0295 §4a), newest first.
 *
 * The World's catalog read exactly as a project's is, because it holds exactly
 * the same entries. `[]` for a World written before this field, which is every
 * World today, so nothing changes for a deployment that never installs one.
 */
export function worldApps(worldDoc: unknown): AppSummary[] {
  return projectApps(worldDoc)
}
/**
 * Everything a surface can see: the World's apps and this project's, merged
 * (RFC 0295 §4b).
 *
 * THE PROJECT WINS on a clash of ids. It is the more specific statement, and
 * the alternative surprises: a team that installed something into a repository
 * deliberately should not find a deployment-wide default silently in front of
 * it.
 *
 * ASK FOR THIS, NOT FOR A CATALOG. A caller reaching `projectApps` directly is
 * a caller that cannot see a World app, which is the failure mode this function
 * exists to remove. `projectApps` stays exported because "what did THIS project
 * install" is still a real question, for an install surface that has to say
 * where an entry lives.
 */
export function installedApps(
  worldDoc: unknown,
  projectDoc: unknown,
): AppSummary[] {
  const project = projectApps(projectDoc).map(
    (a): AppSummary => ({ ...a, scope: 'project' }),
  )
  const shadowed = new Set(project.map((a) => a.id))
  return [
    ...project,
    ...worldApps(worldDoc)
      .filter((a) => !shadowed.has(a.id))
      .map((a): AppSummary => ({ ...a, scope: 'world' })),
  ]
}
/**
 * What an app's settings ARE, right now (RFC 0284 S6g).
 *
 * The declared spec, with whatever anybody set laid over it. Two structures
 * merged at read time rather than one written together, because the spec is the
 * app author's and the values are the person's: re-declaring a field on an
 * update must not reset it, and a value for a field the app no longer declares
 * must not appear as a setting nobody can explain.
 *
 * A SETTING THE APP NO LONGER DECLARES IS DROPPED. Its value stays on the
 * catalog entry, so downgrading the app or re-adding the field gets it back,
 * and nothing reads it in the meantime.
 */
export function appSettings(
  app: Pick<AppRef, 'settings' | 'settingValues'> | undefined,
): Record<string, SettingValue> {
  const out: Record<string, SettingValue> = {}
  for (const spec of app?.settings ?? []) {
    if (!spec || typeof spec.key !== 'string') continue
    const set = app?.settingValues?.[spec.key]
    // TYPE-CHECKED on the way out. A catalog entry is a synced document, so its
    // values are whatever was written; a string where a number is declared
    // would reach a fill as one and break it far from here.
    if (matchesType(set, spec.type)) out[spec.key] = set
    else if (matchesType(spec.default, spec.type)) out[spec.key] = spec.default
  }
  return out
}
function matchesType(
  value: unknown,
  type: SettingSpec['type'],
): value is SettingValue {
  if (type === 'number')
    return typeof value === 'number' && !Number.isNaN(value)
  if (type === 'boolean') return typeof value === 'boolean'
  return typeof value === 'string'
}
/**
 * WHO OPENS a mime or a content kind (RFC 0284 S6m).
 *
 * Desktop's "Open With", and the three cases are the whole design:
 *
 *   chosen     the person named one. It wins even if others claim the key,
 *              and `''` names the built-in, which is how they go back.
 *   only       exactly one package claims it and nobody chose. Use it: an app
 *              that installs and never opens anything is not an install.
 *   builtin    nobody claims it, OR several do and nobody chose. The second is
 *              the case that matters: picking one by map order is a coin flip,
 *              and a coin flip is the one answer that is never right.
 *
 * A NAMED APP THAT IS GONE falls back to the same rules rather than erroring.
 * Uninstalling something you had chosen is ordinary, and a window that refused
 * to draw a document because a preference dangles would be worse than any
 * editor it could have picked.
 */
export interface HandlerChoice {
  /** Which rule decided, so a surface can say WHY rather than only what. */
  source: 'chosen' | 'only' | 'builtin'
  /** The app to use, or undefined for the built-in. */
  app?: AppSummary
  /** Everything that claims this key, in catalog order. What a picker offers,
   *  and what makes `builtin` mean "ambiguous" rather than "nothing". */
  claimants: AppSummary[]
}
export function resolveHandler(
  apps: readonly AppSummary[],
  key: string,
  handlers: Record<string, string> | undefined,
): HandlerChoice {
  const claimants = apps.filter((a) => a.handles?.includes(key))
  const chosen = handlers?.[key]
  if (typeof chosen === 'string') {
    // THE EMPTY STRING IS A CHOICE, not an absent one: it is how a person says
    // "the built-in", which they cannot say by deleting the key because that
    // means undecided and would hand them back a single claimant.
    if (chosen === '') return { source: 'chosen', claimants }
    const app = claimants.find((a) => a.id === chosen)
    if (app) return { source: 'chosen', app, claimants }
  }
  if (claimants.length === 1)
    return { source: 'only', app: claimants[0], claimants }
  return { source: 'builtin', claimants }
}
/** Every key anything installed claims, sorted, so a picker can offer one row
 *  per type without knowing what types exist. */
export function handledKeys(apps: readonly AppSummary[]): string[] {
  const keys = new Set<string>()
  for (const app of apps) for (const k of app.handles ?? []) keys.add(k)
  return [...keys].sort()
}
/**
 * The layouts among some installed apps (RFC 0284 §4e), in the order given.
 *
 * A filter over catalog entries rather than a second map, because a layout IS a
 * catalog entry. The alternative was `ProjectDoc.layouts`, which would give
 * installing, listing, sharing and uninstalling a second implementation each,
 * for a thing the catalog already does.
 *
 * TAKES THE APPS, NOT A DOCUMENT, and that is the whole reason this signature
 * looks indirect. A layout arranges a WINDOW, a window binds to a World
 * (RFC 0281), and a World holds several projects (RFC 0290) — so a layout is
 * normally installed in the World catalog, which RFC 0295 adds. Taking a
 * project document here would have hard-coded the one scope a layout is least
 * likely to live in, and every caller would have had to be found again later.
 * The caller decides what "installed" means and hands the result in.
 */
export function layoutsIn(apps: readonly AppSummary[]): AppSummary[] {
  return apps.filter((a) => a.presentation === 'layout' && !!a.layout)
}
const SLOTS: readonly Slot[] = [
  'sidebar',
  'panel',
  'header',
  'composer',
  'sessionHeader',
]

/**
 * Slot fills as an app declared them, validated for the catalog.
 *
 * Unknown kinds, slots and projections are DROPPED, not rejected, so an app
 * registered by a newer client still installs on an older one, minus the fill
 * it cannot draw. What is kept keeps its slot and, for an `items` fill, the
 * projection it selects: a fill stripped to `{id, title, kind}` lands in the
 * panel slot and a Review `sessionChanges` declaration would vanish.
 */
export function readSlotFills(raw: unknown): SlotFill[] {
  if (!Array.isArray(raw)) return []
  const fills: SlotFill[] = []
  for (const entry of raw) {
    const p = (entry ?? {}) as Record<string, unknown>
    // Strings off the log can be Automerge ImmutableStrings.
    const id = p.id == null ? '' : String(p.id)
    const title = p.title == null ? '' : String(p.title)
    const kind = String(p.kind ?? '')
    if (!id || !title || (kind !== 'view' && kind !== 'items')) continue
    const slot = p.slot == null ? undefined : String(p.slot)
    if (slot !== undefined && !SLOTS.includes(slot as Slot)) continue
    const fill: SlotFill = {
      id,
      title,
      kind,
      ...(slot ? { slot: slot as Slot } : {}),
    }
    if (kind === 'items') {
      if (String(p.projection ?? '') !== 'sessionChanges') continue
      fill.projection = 'sessionChanges'
      if (String(p.action ?? '') === 'reviewBranch')
        fill.action = 'reviewBranch'
    }
    const rows = Number(p.initialRows)
    if (Number.isInteger(rows) && rows > 0) fill.initialRows = rows
    fills.push(fill)
  }
  return fills
}
/**
 * What fills a slot, per a layout, or `undefined` for "the layout does not say".
 *
 * UNDEFINED AND EMPTY ARE DIFFERENT, and conflating them is the bug this
 * function exists to prevent. `undefined` means unspecified, and the caller
 * falls back to the built-in. `[]` means the person emptied the slot, and the
 * caller renders nothing. A resolver returning `[]` for both would make "I do
 * not want a sidebar" indistinguishable from "this layout is silent about
 * sidebars", and the second one has to keep working for every World written
 * before layouts existed.
 */
export function slotFill(
  layout: Layout | undefined,
  slot: Slot,
): string[] | undefined {
  const fill = layout?.slots?.[slot]
  return Array.isArray(fill) ? fill : undefined
}
/**
 * The panes a layout opens a space on (RFC 0284 S6h), or undefined for none.
 *
 * Undefined rather than a fallback string, because the caller already has one:
 * a space with no layout tree opens on the picker, and putting that constant
 * here would move an app decision into the model.
 */
export function layoutTree(layout: Layout | undefined): string | undefined {
  const tree = layout?.tree
  // A synced document holds whatever was written. An empty string would decode
  // to nothing and render a blank window, which is worse than the picker.
  return typeof tree === 'string' && tree.length > 0 ? tree : undefined
}
/**
 * How a slot is SHAPED, per a layout (RFC 0284 S6d).
 *
 * Always an object, unlike `slotFill`, because there is nothing here for the
 * absent case to mean: a surface with no stated size opens at the shell's
 * default and a surface with no stated reveal is pinned. `slotFill` has to
 * distinguish absent from empty; this has one fallback and can just apply it.
 */
export function slotGeometry(
  layout: Layout | undefined,
  slot: Slot,
): { size?: number; reveal: 'pinned' | 'hover' } {
  const g = layout?.geometry?.[slot]
  return {
    // A size of 0 is not a size. It would collapse the surface to nothing while
    // claiming to be pinned, which is what emptying the fill is for.
    size: typeof g?.size === 'number' && g.size > 0 ? g.size : undefined,
    reveal: g?.reveal === 'hover' ? 'hover' : 'pinned',
  }
}
/**
 * The layout a project is arranged by, given the id naming it.
 *
 * `undefined` for an id that names nothing, which is the uninstalled case and is
 * a normal state rather than an error: a layout can be uninstalled while a space
 * still points at it. The caller falls back to the built-in default, which is
 * the same path a World that never had a layout takes.
 */
export function resolveLayout(
  apps: readonly AppSummary[],
  layoutId: string | null | undefined,
): Layout | undefined {
  if (!layoutId) return undefined
  return layoutsIn(apps).find((a) => a.id === layoutId)?.layout
}
/**
 * Project a project's installed capabilities (RFC 0264 &sect;4): the generic
 * install registry, what is installed and where each capability's own doc lives.
 * `[]` for a project that installed nothing (docs-only). A host reads this to
 * know whether to wire vcs and, via `docId`, where its `VcsDoc` is.
 */
export function projectInstalledCapabilities(
  projectDoc: unknown,
): CapabilityRef[] {
  if (!projectDoc || typeof projectDoc !== 'object') return []
  const map = (projectDoc as { capabilities?: Record<string, unknown> })
    .capabilities
  if (!map || typeof map !== 'object') return []
  const out: CapabilityRef[] = []
  for (const [name, raw] of Object.entries(map)) {
    if (!raw || typeof raw !== 'object') continue
    const docId = projectString((raw as { docId?: unknown }).docId)
    if (docId === undefined) continue
    out.push({ name, docId })
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}
